import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Loader2 } from './Icons';
import { User, GeneratedBook, ParentControls, GENRES } from '../types';
import { storyService } from '../services/storyService';
import { buildParentInsights } from '../services/storyInsights';
import { parentControlsService } from '../services/parentControlsService';

interface ParentDashboardProps {
  user: User;
  childName?: string;
  onGoHome: () => void;
}

const ParentDashboard: React.FC<ParentDashboardProps> = ({ user, childName, onGoHome }) => {
  const [stories, setStories] = useState<GeneratedBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [controls, setControls] = useState<ParentControls | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const insights = useMemo(() => buildParentInsights(stories, childName), [stories, childName]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      const [data, ctrl] = await Promise.all([
        storyService.getStories(user.id),
        parentControlsService.getControls(user.id),
      ]);
      if (mounted) {
        setStories(data);
        setControls(ctrl || {
          userId: user.id,
          maxStoryLength: 'Long',
          excludedThemes: [],
          monthlyStoryLimit: 30,
          dailySoftLimit: 5,
        });
        setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [user.id]);

  const handleSaveControls = async () => {
    if (!controls) return;
    setSaving(true);
    try {
      await parentControlsService.saveControls(controls);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-20 flex justify-center text-white">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-10 animate-slide-up pb-24 max-w-3xl">
      <button
        onClick={onGoHome}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-4"
      >
        ← Home
      </button>

      <h2 className="text-4xl font-serif font-bold text-white mb-1">Parent Dashboard</h2>
      <p className="text-gray-400 mb-10 text-sm">Read-only insights from {childName ? `${childName}'s` : 'your'} story library. No scores, no pressure.</p>

      {stories.length === 0 ? (
        <div className="text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-500">
            <BookOpen className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No stories yet</h3>
          <p className="text-gray-400">Insights will appear here once you've generated a few stories.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Themes / Tones / Values row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-3">Themes Read</p>
              {insights.topThemes.length > 0 ? (
                <ul className="space-y-1">
                  {insights.topThemes.map((t) => (
                    <li key={t.label} className="text-white font-medium text-sm">{t.label} <span className="text-gray-500 font-normal">×{t.count}</span></li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm">Not enough data yet</p>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-3">Emotional Tones</p>
              {insights.topTones.length > 0 ? (
                <ul className="space-y-1">
                  {insights.topTones.map((t) => (
                    <li key={t.label} className="text-white font-medium text-sm">{t.label} <span className="text-gray-500 font-normal">×{t.count}</span></li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm">Not enough data yet</p>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-3">Values Reinforced</p>
              {insights.topValues.length > 0 ? (
                <ul className="space-y-1">
                  {insights.topValues.map((v) => (
                    <li key={v.label} className="text-white font-medium text-sm">{v.label} <span className="text-gray-500 font-normal">×{v.count}</span></li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-sm">Not enough data yet</p>
              )}
            </div>
          </div>

          {/* Explainability cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-gold-500/30 bg-gold-500/10 p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gold-300 mb-2">Why These Stories Were Generated</p>
              <p className="text-sm text-white/90">{insights.whyGenerated}</p>
            </div>
            <div className="rounded-2xl border border-spark-500/30 bg-spark-500/10 p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-spark-300 mb-2">Primary Value Emphasized</p>
              <p className="text-sm text-white/90">{insights.emphasizedValue}</p>
            </div>
          </div>

          {/* Library summary */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-1">Stories Saved</p>
              <p className="text-3xl font-bold text-white">{stories.length}<span className="text-gray-500 text-base font-normal"> / 10</span></p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-1">Most Recent</p>
              <p className="text-sm text-white font-medium">{stories[0]?.title || '—'}</p>
              <p className="text-xs text-gray-500">{stories[0]?.createdAt ? new Date(stories[0].createdAt).toLocaleDateString() : ''}</p>
            </div>
          </div>

          <p className="text-xs text-gray-600 text-center pt-2">
            World memory and cross-session learning arrive in v2.1. These insights are generated fresh each session from your story library.
          </p>

          {/* Story Controls */}
          {controls && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 space-y-6">
              <h3 className="text-lg font-semibold text-white">Story Controls</h3>

              {/* Max story length */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Max story length allowed</label>
                <p className="text-xs text-gray-500 mb-3">Stories longer than this won't be generated.</p>
                <div className="flex gap-2">
                  {(['Short', 'Medium', 'Long'] as const).map(len => (
                    <button
                      key={len}
                      onClick={() => setControls({ ...controls, maxStoryLength: len })}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                        controls.maxStoryLength === len
                          ? 'bg-gold-500 text-black'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >{len}</button>
                  ))}
                </div>
              </div>

              {/* Excluded themes */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Block these themes</label>
                <p className="text-xs text-gray-500 mb-3">Selected themes will never appear in generated stories.</p>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map(genre => {
                    const blocked = controls.excludedThemes.includes(genre);
                    return (
                      <button
                        key={genre}
                        onClick={() => setControls({
                          ...controls,
                          excludedThemes: blocked
                            ? controls.excludedThemes.filter(t => t !== genre)
                            : [...controls.excludedThemes, genre],
                        })}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          blocked
                            ? 'bg-red-500/30 text-red-300 border border-red-500/50'
                            : 'bg-white/10 text-gray-300 hover:bg-white/20'
                        }`}
                      >{blocked ? `✕ ${genre}` : genre}</button>
                    );
                  })}
                </div>
              </div>

              {/* Monthly cap */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Monthly story limit: <span className="text-white font-semibold">{controls.monthlyStoryLimit}</span>
                </label>
                <p className="text-xs text-gray-500 mb-3">Generation is blocked once this many stories have been created this month.</p>
                <input
                  type="range" min={1} max={100} value={controls.monthlyStoryLimit}
                  onChange={e => setControls({ ...controls, monthlyStoryLimit: Number(e.target.value) })}
                  className="w-full accent-gold-500"
                />
              </div>

              <button
                onClick={handleSaveControls}
                disabled={saving}
                className="w-full py-3 rounded-xl bg-gold-500 hover:bg-gold-400 text-black font-semibold transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Controls'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ParentDashboard;
